import type * as React from "react";
import { Box, Typography } from "@mui/material";
import GiftHowItWorksArrowhead from "assets/images/gift-how-it-works-arrowhead.svg";
import { COLORS } from "components/v3/Theme/colors";

export type HorizontalHowItWorksStepContent = {
  stepKey: string;
  title: string;
  body: React.ReactNode;
  bodySecondary?: React.ReactNode;
  icon: React.ReactNode;
};

export type HorizontalHowItWorksStepsProps = {
  sectionTitle: string;
  sectionSubtitle?: string;
  steps: readonly HorizontalHowItWorksStepContent[];
  circleSizePx?: number;
};

const defaultCircleSizePx = 56;

const collapsedStepNumberCirclePx = 24;

const HorizontalHowItWorksSteps: React.FC<HorizontalHowItWorksStepsProps> = ({
  sectionTitle,
  sectionSubtitle,
  steps,
  circleSizePx = defaultCircleSizePx
}) => {
  const stepCircleSx = {
    alignItems: "center",
    bgcolor: COLORS.WHITE,
    borderRadius: "50%",
    display: "flex",
    flexShrink: 0,
    height: circleSizePx,
    justifyContent: "center",
    width: circleSizePx
  } as const;

  const connectorRowSx = {
    alignItems: "center",
    display: "flex",
    flex: 1,
    height: circleSizePx,
    minWidth: 0
  } as const;

  const desktopGridSx = {
    display: { md: "grid", xs: "none" },
    gap: 2,
    gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`,
    width: "100%"
  } as const;

  return (
    <Box
      sx={{
        bgcolor: COLORS.BACKGROUND_BLUE,
        borderRadius: "24px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: { md: 5, xs: 3 },
        width: "100%"
      }}
    >
      <Typography component="h2" variant="h4_black">
        {sectionTitle}
      </Typography>
      {sectionSubtitle ? (
        <Typography component="p" variant="body_lg" sx={{ m: 0, mb: 3 }}>
          {sectionSubtitle}
        </Typography>
      ) : null}

      <Box sx={{ ...desktopGridSx, mt: 1 }}>
        {steps.map((step, index) => (
          <Box
            key={`${step.stepKey}-icon-row`}
            sx={{
              alignItems: "center",
              columnGap: 1.5,
              display: "flex",
              minWidth: 0
            }}
          >
            <Box sx={stepCircleSx}>{step.icon}</Box>
            {index < steps.length - 1 ? (
              <Box aria-hidden sx={connectorRowSx}>
                <Box
                  sx={{
                    bgcolor: COLORS.PRIMARY_40,
                    borderRadius: "2px",
                    flex: 1,
                    height: 2,
                    minWidth: 4
                  }}
                />
                <Box
                  alt=""
                  component="img"
                  src={GiftHowItWorksArrowhead}
                  sx={{
                    display: "block",
                    flexShrink: 0,
                    height: 12,
                    marginLeft: "-2px",
                    width: "auto"
                  }}
                />
              </Box>
            ) : null}
          </Box>
        ))}
      </Box>

      <Box sx={{ ...desktopGridSx, mt: 2 }}>
        {steps.map((step) => (
          <Box key={`${step.stepKey}-copy`} sx={{ minWidth: 0 }}>
            <Typography component="h3" variant="body_strong_black">
              {step.title}
            </Typography>
            <Typography
              component="p"
              sx={{ color: COLORS.DARK_GRAY, mt: 1 }}
              variant="body2"
            >
              {step.body}
            </Typography>
            {step.bodySecondary ? (
              <Typography
                component="p"
                sx={{ color: COLORS.DARK_GRAY, mt: 1.5 }}
                variant="body2"
              >
                {step.bodySecondary}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: { md: "none", xs: "flex" },
          flexDirection: "column",
          gap: 3,
          mt: 1
        }}
      >
        {steps.map((step, index) => (
          <Box
            key={`${step.stepKey}-mobile`}
            sx={{
              alignItems: "flex-start",
              display: "flex",
              flexDirection: "column",
              minWidth: 0
            }}
          >
            <Box
              aria-label={`Step ${index + 1} of ${steps.length}`}
              sx={{
                alignItems: "center",
                bgcolor: COLORS.ACCENT2,
                borderRadius: "50%",
                display: "flex",
                flexShrink: 0,
                height: collapsedStepNumberCirclePx,
                justifyContent: "center",
                width: collapsedStepNumberCirclePx
              }}
            >
              <Typography
                component="span"
                sx={{
                  color: COLORS.WHITE,
                  fontSize: "16px",
                  fontWeight: 700,
                  lineHeight: 1,
                  transform: "translateY(1px)"
                }}
              >
                {index + 1}
              </Typography>
            </Box>
            <Typography
              component="h3"
              sx={{ mt: 2 }}
              variant="body_strong_black"
            >
              {step.title}
            </Typography>
            <Typography
              component="p"
              sx={{ color: COLORS.DARK_GRAY_2, mt: 1 }}
              variant="body2"
            >
              {step.body}
            </Typography>
            {step.bodySecondary ? (
              <Typography
                component="p"
                sx={{ color: COLORS.DARK_GRAY, mt: 1.5 }}
                variant="body2"
              >
                {step.bodySecondary}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export function horizontalHowItWorksImgIcon(
  src: string,
  sizePx: number
): React.ReactNode {
  return (
    <Box
      alt=""
      aria-hidden
      component="img"
      height={sizePx}
      src={src}
      width={sizePx}
    />
  );
}

export default HorizontalHowItWorksSteps;
